import { App } from '@capacitor/app';
import { useCallback, useEffect, useRef, useState } from 'react';

import { refresh as refreshTokenReq } from '../../axios/Utils/interceptors';
import { AUTH_EVENTS, authEvents } from '../../utils/authEvents';
import { clearActiveDiagnosticsAccess } from '../../utils/diagnosticsAccess';
import { StorageService } from '../../utils/storage';
import { STORAGE_KEYS } from './useAuth';

/**
 * Verifica si un token JWT está expirado
 * @param token - Token JWT a verificar
 * @param marginMs - Margen de tiempo en ms antes de la expiración (default: 60s)
 */
const isTokenExpired = (token: string, marginMs = 60000): boolean => {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= exp * 1000 - marginMs;
  } catch {
    return true;
  }
};

export const useSessionManager = () => {
  const [authStatus, setAuthStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const isRestoringRef = useRef<boolean>(false);

  const clearSession = useCallback(async () => {
    clearActiveDiagnosticsAccess();
    await StorageService.clearAuthKeys(Object.values(STORAGE_KEYS));
    setAuthStatus(false);
  }, []);

  /**
   * Refresca los tokens usando el refresh token almacenado
   */
  const refreshTokens = useCallback(async (): Promise<boolean> => {
    const refreshToken = await StorageService.getSecure(STORAGE_KEYS.refreshToken);

    if (!refreshToken) return false;

    // Validar si el refresh token también expiró
    if (isTokenExpired(refreshToken)) {
      return false;
    }

    const result = await refreshTokenReq({ refreshToken });
    if (!result.ok || !result.data) return false;

    await Promise.all([
      StorageService.setSecure(STORAGE_KEYS.token, result.data.token),
      StorageService.setSecure(STORAGE_KEYS.refreshToken, result.data.refreshToken),
    ]);

    return true;
  }, []);

  /**
   * Restaura la sesión verificando tokens almacenados
   */
  const restoreSession = useCallback(async (): Promise<void> => {
    // Evitar restauraciones concurrentes
    if (isRestoringRef.current) return;
    isRestoringRef.current = true;

    try {
      const [token, refreshToken] = await Promise.all([
        StorageService.getSecure(STORAGE_KEYS.token),
        StorageService.getSecure(STORAGE_KEYS.refreshToken),
      ]);

      // Sin tokens almacenados
      if (!token || !refreshToken) {
        clearActiveDiagnosticsAccess();
        setAuthStatus(false);
        return;
      }

      // Refresh token expirado - no tiene sentido intentar refresh
      if (isTokenExpired(refreshToken)) {
        await clearSession();
        return;
      }

      // Token válido
      if (!isTokenExpired(token)) {
        setAuthStatus(true);
        return;
      }

      // Token expirado pero refresh token válido - intentar refresh
      const refreshed = await refreshTokens();
      if (refreshed) {
        setAuthStatus(true);
      } else {
        await clearSession();
      }
    } catch (error) {
      console.error('[useSessionManager] Error restoring session:', error);
      await clearSession();
    } finally {
      isRestoringRef.current = false;
      setLoading(false);
    }
  }, [clearSession, refreshTokens]);

  // Efecto inicial: restaurar sesión al montar
  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // Efecto: escuchar eventos de auth (session-expired)
  useEffect(() => {
    const unsubscribe = authEvents.on(AUTH_EVENTS.SESSION_EXPIRED, async () => {
      await clearSession();
    });

    return () => {
      unsubscribe();
    };
  }, [clearSession]);

  // Efecto: manejar app resume (volver del background)
  useEffect(() => {
    let listenerHandle: { remove: () => Promise<void> } | null = null;

    const setupListener = async () => {
      listenerHandle = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive && authStatus) {
          // App volvió del background y hay sesión activa
          // Re-validar que la sesión siga siendo válida
          const token = await StorageService.getSecure(STORAGE_KEYS.token);

          if (!token) {
            await clearSession();
            return;
          }

          // Si el token expiró mientras estaba en background, intentar refresh
          if (isTokenExpired(token)) {
            const refreshed = await refreshTokens();
            if (!refreshed) {
              await clearSession();
            }
          }
        }
      });
    };

    setupListener();

    return () => {
      listenerHandle?.remove();
    };
  }, [authStatus, clearSession, refreshTokens]);

  return { authStatus, setAuthStatus, loading, clearSession };
};
